import React, { useState, useEffect } from 'react';
import {
  Info, X, Check, ArrowRight, UsersThree, ClockCounterClockwise, ArrowClockwise, Minus, Plus,
} from '@phosphor-icons/react';
import {
  getContactById,
  getContactByMobile,
  updateTotalChits,
  saveData,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';

// ── Minimal stacked-button number input, standing in for PrimeNG p-inputNumber ──
// Keeps its own local text state so every keystroke is reflected immediately;
// clamping to min/max happens on blur (and instantly via the +/- buttons) rather
// than on every keystroke, which previously could make typing look like a no-op.
function StackedNumberInput({ value, onChange, min = 0, max = 10, testId }) {
  const [text, setText] = useState(String(value ?? 0));

  useEffect(() => {
    setText(String(value ?? 0));
  }, [value]);

  const step = (delta) => {
    const next = Math.min(max, Math.max(min, Number(value || 0) + delta));
    onChange(next);
  };

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    setText(raw);
    if (raw === '') {
      onChange(0);
      return;
    }
    onChange(Math.min(max, Math.max(min, Number(raw))));
  };

  const handleBlur = () => {
    const clamped = Math.min(max, Math.max(min, Number(text || 0)));
    setText(String(clamped));
    onChange(clamped);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }} data-testid={testId}>
      <input
        className="input-field"
        style={{ fontSize: '13px', borderTopRightRadius: 0, borderBottomRightRadius: 0, textAlign: 'right' }}
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #D1D5DB', borderLeft: 'none', borderRadius: '0 6px 6px 0', overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => step(1)}
          style={{ flex: 1, border: 'none', borderBottom: '1px solid #D1D5DB', background: '#F9FAFB', cursor: 'pointer', padding: '2px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Plus size={10} />
        </button>
        <button
          type="button"
          onClick={() => step(-1)}
          style={{ flex: 1, border: 'none', background: '#F9FAFB', cursor: 'pointer', padding: '2px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Minus size={10} />
        </button>
      </div>
    </div>
  );
}

function MultipleContactsForm() {
  const loginName = useLoginName();
  const [contactReferenceId, setContactReferenceId] = useState('');
  const [businessEntityContactNo, setBusinessEntityContactNo] = useState('');
  const [maxChitsPerContact, setMaxChitsPerContact] = useState(0);
  const [additionalChits, setAdditionalChits] = useState(0);
  const [totalcount, setTotalcount] = useState(0);
  const [reason, setReason] = useState('');

  const [oldMaxChits, setOldMaxChits] = useState(null);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [pendingReason, setPendingReason] = useState('');
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pendingAudit, setPendingAudit] = useState(null);

  // Mirrors calculateTotal(), driven off additionalChits / max_chits_per_contact changes
  useEffect(() => {
    const existed = Number(maxChitsPerContact || 0);
    const additional = Number(additionalChits || 0);

    if (additional > 10) {
      setAdditionalChits(10);
      return;
    }

    setTotalcount(existed + additional);
  }, [additionalChits, maxChitsPerContact]);

  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = reason.trim().length >= 10 && reason.length <= 200;

  const onContactIdBlur = async () => {
    if (!contactReferenceId) return;
    try {
      const res = await getContactById(contactReferenceId);
      const data = res?.find((x) => String(x.contact_reference_id) === String(contactReferenceId));
      if (!data) {
        showToast('warn', 'Not Found', 'Contact not found');
        return;
      }
      setBusinessEntityContactNo(data.business_entity_contactno);
      setMaxChitsPerContact(data.max_chits_per_contact);
      setOldMaxChits(Number(data.max_chits_per_contact || 0));
    } catch {
      showToast('error', 'Load Failed', 'Could not look up the contact.');
    }
  };

  const onMobileBlur = async () => {
    if (!businessEntityContactNo || businessEntityContactNo.length !== 10) {
      showToast('warn', 'Invalid Mobile', 'Enter valid mobile number');
      return;
    }
    try {
      const res = await getContactByMobile(businessEntityContactNo);
      const data = res?.find((x) => String(x.business_entity_contactno) === String(businessEntityContactNo));
      if (!data) {
        showToast('warn', 'Not Found', 'Mobile not found');
        return;
      }
      setContactReferenceId(data.contact_reference_id);
      setMaxChitsPerContact(data.max_chits_per_contact);
      setOldMaxChits(Number(data.max_chits_per_contact || 0));
    } catch {
      showToast('error', 'Load Failed', 'Could not look up the mobile number.');
    }
  };

  const allowNumbersOnly = (e) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  // Step 1: validate + stage data, open confirm modal
  const handleConfirmSave = () => {
    setTouched(true);
    if (!isValid) return;

    if (!contactReferenceId) {
      showToast('warn', 'Incomplete Form', 'Enter Contact ID');
      return;
    }

    if (oldMaxChits === null) {
      showToast('warn', 'Contact Not Verified', 'Please look up a valid contact ID or mobile number first.');
      return;
    }

    if (oldMaxChits === totalcount) {
      showToast('warn', 'No Changes', 'No changes detected');
      return;
    }

    setPendingPayload({
      referenceid: contactReferenceId,
      totalcount,
    });

    setPendingReason(reason);

    setPendingChanges([
      {
        label: 'Total Chits',
        oldValue: String(oldMaxChits ?? ''),
        newValue: String(totalcount),
      },
    ]);

    setPendingAudit({
      schemaName: 'default',
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'MULTI_CONTACTS',
      oldData: { max_chits_per_contact: oldMaxChits },
      newData: { max_chits_per_contact: totalcount },
      reason,
    });

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  // Step 2: fires only after user clicks "Yes, Save" in the modal
  const doSave = async () => {
    if (!pendingPayload || !pendingAudit) return;

    setSaving(true);
    try {
      await updateTotalChits(pendingPayload);
      try {
        await saveData([pendingAudit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Saved successfully & audit logged.', true);
        handleClear();
      } catch {
        setSaving(false);
        setShowConfirmModal(false);
        showToast('warn', 'Partial Success', 'Saved, but audit log failed to save.', true);
      }
    } catch {
      setSaving(false);
      setShowConfirmModal(false);
      showToast('error', 'Save Failed', 'Could not save the changes. Please try again.', true);
    }
  };

  const handleClear = () => {
    setContactReferenceId('');
    setBusinessEntityContactNo('');
    setMaxChitsPerContact(0);
    setAdditionalChits(0);
    setTotalcount(0);
    setReason('');
    setOldMaxChits(null);
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="multi-contacts-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="multi-contacts-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UsersThree size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Multiple Contacts</div>
            </div>
          </div>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ClockCounterClockwise size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: Contact Lookup */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Contact Lookup</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Enter either field and tab out to fetch the contact's chit details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Contact ID <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              type="text"
              className="input-field"
              style={{ fontSize: '13px' }}
              maxLength={20}
              placeholder="Enter contact ID"
              value={contactReferenceId}
              // maxLength alone does not cover a paste on some browsers, so the
              // value is truncated here too.
              onChange={(e) => setContactReferenceId(e.target.value.slice(0, 20))}
              onBlur={onContactIdBlur}
              data-testid="multi-contact-id"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Mobile No. <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              type="text"
              className="input-field"
              style={{ fontSize: '13px' }}
              maxLength={10}
              placeholder="Enter 10-digit mobile no."
              value={businessEntityContactNo}
              onKeyPress={allowNumbersOnly}
              onChange={(e) => setBusinessEntityContactNo(e.target.value)}
              onBlur={onMobileBlur}
              data-testid="multi-mobile-no"
            />
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={oldMaxChits === null} hint="Look up a valid contact ID or mobile number in section A first">
        {/* SECTION B: Chit Adjustment */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Chit Adjustment</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Add up to 10 additional chits for this contact</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>No. of Existed Chits</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={maxChitsPerContact} readOnly data-testid="multi-existing-chits" />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>No. of Required Additional Chits</label>
            <StackedNumberInput
              value={additionalChits}
              onChange={setAdditionalChits}
              min={0}
              max={10}
              testId="multi-additional-chits"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Total Chits</label>
            <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB', fontWeight: '600' }} value={totalcount} readOnly data-testid="multi-total-chits" />
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={oldMaxChits === null} hint="Look up a valid contact ID or mobile number in section A first">
        {/* SECTION C: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="multi-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : reason.trim().length < 10 ? 'Minimum 10 characters required' : 'Maximum 200 characters allowed'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="multi-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleConfirmSave} className="btn-primary" disabled={saving} data-testid="multi-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="multi-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <UsersThree size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Chit Count Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You're about to update the total chit count for this contact. Please review before confirming.
              </p>

              {pendingChanges.map((change) => (
                <div key={change.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (Old)</p>
                    <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{change.oldValue}</p>
                  </div>
                  <ArrowRight size={14} color="#9CA3AF" />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (New)</p>
                    <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{change.newValue}</p>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="multi-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={saving} className="btn-primary" data-testid="multi-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MultipleContactsForm;